import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { acquireSlot, releaseSlot } from './concurrency';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - serialize (host EP limit) + attach auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    await acquireSlot();
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - release slot + handle auth errors globally
api.interceptors.response.use(
  (response: AxiosResponse) => {
    releaseSlot();
    return response;
  },
  (error) => {
    releaseSlot();
    const url = error?.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || (url.includes('/auth/profile') && error?.config?.method === 'get');

    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('subscription');
      const pathname = window.location.pathname;
      if (!pathname.includes('/login')) {
        window.location.href = `${process.env.PUBLIC_URL || ''}/login`;
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth Services ───────────────────────────────────────────────
export const authService = {
  register: (data: Record<string, any>) => api.post('/auth/register', data),

  login: (data: Record<string, any>) => api.post('/auth/login', data),

  /** Auto-login for demo — returns token + user instantly */
  demoLogin: () => api.post('/auth/demo-login'),

  getProfile: () => api.get('/auth/profile'),

  updateProfile: (data: {
    name?: string;
    email?: string;
    staffName?: string;
    storeType?: string;
    phoneNumber?: string;
    currentPassword?: string;
    newPassword?: string;
  }) => api.put('/auth/profile', data),
};

// ─── Customer Services ───────────────────────────────────────────
export const customerService = {
  checkCustomer: (cellNumber: string) =>
    api.post('/customers/check', { cellNumber }),

  createOrUpdate: (data: Record<string, any>) => api.post('/customers', data),

  getAll: (page: number = 1, limit: number = 20, search: string = '') =>
    api.get('/customers', { params: { page, limit, search } }),

  // Full list for CSV export (server skips pagination when all=true).
  getAllForExport: (search: string = '') =>
    api.get('/customers', { params: { all: 'true', search } }),

  getById: (id: string) => api.get(`/customers/${id}`),

  getRepeated: (page: number = 1, limit: number = 20) =>
    api.get('/customers/repeated', { params: { page, limit } }),

  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/customers/${id}`, data),

  delete: (id: string) => api.delete(`/customers/${id}`),

  // Manual VIP / Ideal marks. Additive to the automatic segmentation rules.
  setTags: (id: string, tags: string[]) =>
    api.put(`/customers/${id}/tags`, { tags }),

  getSegmentation: () => api.get('/customers/segmentation'),

  getSegmentCustomers: (segment: string, page: number = 1, limit: number = 20) =>
    api.get(`/customers/segment/${segment}`, { params: { page, limit } }),

  getByPhone: (phone: string) =>
    api.get(`/customers/phone/${phone}`),
};

// ─── Shop / Company Profile Services ─────────────────────────────
// The shop's own contact details. `getPublic` is unauthenticated and is used
// by the QR self-registration pages to render the shop name / WhatsApp /
// save-contact QR. `get`/`update` are used by the Profile page.
export const storeService = {
  getPublic: () => api.get('/settings/public'),
  get: () => api.get('/settings/store'),
  update: (data: {
    companyName?: string;
    phone?: string;
    whatsapp?: string;
    website?: string;
    address?: string;
  }) => api.put('/settings/store', data),
};

// ─── Order Services ──────────────────────────────────────────────
export const orderService = {
  create: (data: Record<string, any>) => api.post('/orders', data),

  getAll: (customerId?: string, page: number = 1, limit: number = 20) =>
    api.get('/orders', { params: { customerId, page, limit } }),

  getById: (id: string) => api.get(`/orders/${id}`),

  updateStage: (id: string, data: Record<string, any>) => api.patch(`/orders/${id}/stage`, data),

  approve: (id: string, data?: { notes?: string }) =>
    api.patch(`/orders/${id}/approve`, data),

  getOrdersByStage: (stage: string, page: number = 1, limit: number = 20) =>
    api.get(`/orders/stage/${stage}`, { params: { page, limit } }),

  getStats: () => api.get('/orders/stats'),

  // Manual payment ledger (advance / part-payment / settlement)
  getPayments: (id: string) => api.get(`/orders/${id}/payments`),
  addPayment: (id: string, data: Record<string, any>) => api.post(`/orders/${id}/payments`, data),
  updatePayment: (id: string, paymentId: string, data: Record<string, any>) =>
    api.put(`/orders/${id}/payments/${paymentId}`, data),
  deletePayment: (id: string, paymentId: string) => api.delete(`/orders/${id}/payments/${paymentId}`),
};

// ─── Repair Services ─────────────────────────────────────────────
export const repairService = {
  create: (data: Record<string, any>) => api.post('/repairs', data),

  getAll: (customerId?: string, status?: string, page: number = 1, limit: number = 20) =>
    api.get('/repairs', { params: { customerId, status, page, limit } }),

  getById: (id: string) => api.get(`/repairs/${id}`),

  updateStage: (id: string, data: Record<string, any>) => api.patch(`/repairs/${id}/stage`, data),

  getStats: () => api.get('/repairs/stats'),

  generateQRCode: (customerId: string) =>
    api.get(`/repairs/qrcode/${customerId}`),

  createSubscription: (data: Record<string, any>) => api.post('/repairs/subscription', data),

  // Manual payment ledger (advance / part-payment / settlement)
  getPayments: (id: string) => api.get(`/repairs/${id}/payments`),
  addPayment: (id: string, data: Record<string, any>) => api.post(`/repairs/${id}/payments`, data),
  updatePayment: (id: string, paymentId: string, data: Record<string, any>) =>
    api.put(`/repairs/${id}/payments/${paymentId}`, data),
  deletePayment: (id: string, paymentId: string) => api.delete(`/repairs/${id}/payments/${paymentId}`),
};

// ─── Old Gold Services ───────────────────────────────────────────
export const oldGoldService = {
  create: (data: Record<string, any>) => api.post('/old-gold', data),

  getAll: (page: number = 1, limit: number = 20) =>
    api.get('/old-gold', { params: { page, limit } }),

  getById: (id: string) => api.get(`/old-gold/${id}`),

  process: (id: string, data: Record<string, any>) => api.patch(`/old-gold/${id}/process`, data),

  getStats: () => api.get('/old-gold/stats'),
};

// ─── Old Gold Comprehensive Services ─────────────────────────────
export const ogService = {
  createTransaction: (data: Record<string, any>) => api.post('/og/transactions', data),

  getTransactions: (params: {
    page?: number; limit?: number; status?: string;
    transactionType?: string; search?: string;
    dateFrom?: string; dateTo?: string;
  } = {}) => api.get('/og/transactions', { params }),

  getTransactionById: (id: string) => api.get(`/og/transactions/${id}`),

  updateTransaction: (id: string, data: Record<string, any>) => api.put(`/og/transactions/${id}`, data),

  setPayment: (id: string, data: { amountPaid?: number; balanceAmount?: number; notes?: string }) =>
    api.put(`/og/transactions/${id}/payment`, data),

  updateStatus: (id: string, status: string) => api.patch(`/og/transactions/${id}/status`, { status }),

  approveTransaction: (id: string, data: { managerApproved: boolean; managerName?: string }) =>
    api.patch(`/og/transactions/${id}/approve`, data),

  customerApprove: (id: string, data: { customerApproved: boolean; approvalMethod?: string }) =>
    api.patch(`/og/transactions/${id}/customer-approve`, data),

  deleteTransaction: (id: string) => api.delete(`/og/transactions/${id}`),

  getGoldRate: () => api.get('/og/rates/current'),

  setGoldRate: (data: { rate24K: number; rate22K: number; buybackDiscount?: number }) =>
    api.post('/og/rates', data),

  getStats: () => api.get('/og/stats'),

  getReports: (params: { type?: string; dateFrom?: string; dateTo?: string; date?: string } = {}) =>
    api.get('/og/reports', { params }),

  getSettings: () => api.get('/og/settings'),

  updateSetting: (key: string, value: string) => api.put('/og/settings', { key, value }),
};

// ─── Dashboard Services ──────────────────────────────────────────
export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),

  getSegmentation: () => api.get('/dashboard/segmentation'),

  getSegmentCustomers: (segment: string, page: number = 1, limit: number = 20) =>
    api.get(`/dashboard/segment/${segment}`, { params: { page, limit } }),

  // Full segment list for CSV export.
  getSegmentCustomersForExport: (segment: string) =>
    api.get(`/dashboard/segment/${segment}`, { params: { all: 'true' } }),

  getReminders: () => api.get('/dashboard/reminders'),

  getAnalytics: (params?: {
    startDate?: string;
    endDate?: string;
    metric?: string;
  }) => api.get('/dashboard/analytics', { params }),

  generateAnalytics: (data: {
    startDate: string;
    endDate: string;
    metrics?: string[];
  }) => api.post('/dashboard/analytics/generate', data),
};

// ─── Interaction Services ────────────────────────────────────────
export const interactionService = {
  create: (data: Record<string, any>) => api.post('/interactions', data),

  getByCustomer: (customerId: string, page: number = 1, limit: number = 20) =>
    api.get(`/interactions/customer/${customerId}`, { params: { page, limit } }),

  getStats: () => api.get('/interactions/stats/overview'),
};

// ─── Subscription Services ───────────────────────────────────────
export const subscriptionService = {
  getPlans: () => api.get('/subscriptions/plans'),

  getCurrent: () => api.get('/subscriptions/current'),

  createOrder: (plan: string) =>
    api.post('/subscriptions/create-order', { plan }),

  verifyPayment: (data: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
    plan?: string;
    simulated?: boolean;
  }) => api.post('/subscriptions/verify', data),

  cancel: () => api.post('/subscriptions/cancel'),

  backup: () => api.post('/subscriptions/backup'),

  resetData: () => api.post('/subscriptions/reset-data'),
};

// ─── Exhibition Services ─────────────────────────────────────────
export const exhibitionService = {
  getAll: (page: number = 1, limit: number = 20) =>
    api.get('/exhibitions', { params: { page, limit } }),

  getById: (id: string) => api.get(`/exhibitions/${id}`),

  create: (data: Record<string, any>) => api.post('/exhibitions', data),

  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/exhibitions/${id}`, data),

  delete: (id: string) => api.delete(`/exhibitions/${id}`),

  generateQR: (id: string) => api.get(`/exhibitions/${id}/qr`),
};

export default api;
